// The user's own budget for the personal affordability metrics (src/personal.ts). Kept in this browser only and never
// put in the page's URL (links get shared); the API gets it as my_* query params on the requests that need it.

import type { Budget } from "../shared/types.ts";
export type { Budget };

const KEY = "monitusre.budget.v1";
export const PERSONAL_IDS = ["my_payment_pct", "my_payment"];

export function loadBudget(): Budget | null {
  try {
    const b = JSON.parse(localStorage.getItem(KEY) ?? "null") as Budget | null;
    return b && Number.isFinite(b.income) && b.income > 0 && b.down ? b : null;
  } catch {
    return null;
  }
}

export function saveBudget(b: Budget | null) {
  try {
    if (b) localStorage.setItem(KEY, JSON.stringify(b));
    else localStorage.removeItem(KEY);
  } catch { /* private window: the budget lasts for this visit */ }
}

/** The API's query params for a budget (empty when none). */
export function budgetQuery(b: Budget | null): string {
  if (!b) return "";
  const q = new URLSearchParams({ my_inc: String(b.income) });
  if ("usd" in b.down) q.set("my_down", String(b.down.usd));
  else q.set("my_downpct", String(b.down.share));
  if (b.rate != null) q.set("my_rate", String(b.rate));
  if (b.termYears !== 30) q.set("my_term", String(b.termYears));
  return q.toString();
}

const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });

/** "$120K income · $60K down · 6.25% · 30 yr" */
export function describeBudget(b: Budget, latestRate: number | null): string {
  const down = "usd" in b.down ? `${usd0.format(b.down.usd)} down` : `${Math.round(b.down.share * 1000) / 10}% down`;
  const rate = b.rate != null ? `${b.rate}%` : latestRate != null ? `${latestRate.toFixed(2)}% (avg.)` : "avg. rate";
  return `${usd0.format(b.income)} income · ${down} · ${rate} · ${b.termYears} yr`;
}

/**
 * Read the form. Income and down payment take "$120k" / "1.2m" / "60,000"; a down payment with "%" is a share of
 * the price. A blank rate means the average rate. Returns an error message instead when something is off.
 */
export function readBudget(f: { income: string; down: string; rate: string; term: string }): Budget | string {
  const money = (s: string) => {
    const x = /^\$?(\d+(?:\.\d+)?)([km]?)$/.exec(s.trim().toLowerCase().replace(/[,\s]/g, ""));
    return x ? Number(x[1]) * (x[2] === "k" ? 1e3 : x[2] === "m" ? 1e6 : 1) : null;
  };
  const income = money(f.income);
  if (income == null || income < 1000) return "Enter your yearly household income (e.g. 120k)";
  const d = f.down.trim();
  let down: Budget["down"];
  if (d === "") down = { share: 0.2 };
  else if (d.endsWith("%")) {
    const p = Number(d.slice(0, -1).trim());
    if (!Number.isFinite(p) || p < 0 || p > 100) return "Down payment % must be between 0 and 100";
    down = { share: p / 100 };
  } else {
    const v = money(d);
    if (v == null) return "Enter the down payment as an amount (60k) or a percent (20%)";
    down = { usd: v };
  }
  const r = f.rate.trim().replace(/%$/, "");
  const rate = r === "" ? null : Number(r);
  if (rate != null && (!Number.isFinite(rate) || rate < 0 || rate > 25)) return "Rate must be between 0 and 25%";
  const termYears = Number(f.term) || 30;
  return { income, down, rate, termYears };
}
